import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, User, Eye, EyeOff } from "lucide-react";
import { API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { useAdminAuth } from "@/context/AdminAuthContext";

const AdminLoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAdminAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, {
        username,
        password,
      });

      if (response.data.access_token) {
        // Use context login function to properly update auth state
        login(response.data.access_token, response.data.username);
        toast.success("Innlogget!");
        // Small delay to ensure state is updated before redirect
        setTimeout(() => {
          navigate("/panel050623", { replace: true });
        }, 100);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Feil brukernavn eller passord");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ 
        background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-script mb-2" style={{ color: '#d4af37' }}>
            Masala Indisk
          </h1>
          <p className="text-sm" style={{ color: '#f5e6d3' }}>
            Admin Panel
          </p>
        </div>

        {/* Login Card */}
        <div 
          className="bg-white rounded-2xl shadow-2xl p-8"
          style={{ border: '3px solid #d4af37' }}
        >
          <div className="text-center mb-6">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: '#721111' }}
            >
              <Lock className="h-8 w-8" style={{ color: '#d4af37' }} />
            </div>
            <h2 className="text-2xl font-serif-display font-semibold" style={{ color: '#2d1810' }}>
              Logg Inn
            </h2>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <Label style={{ color: '#2d1810' }}>Brukernavn</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: '#8b4513' }} />
                <Input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="admin"
                  className="pl-10 indian-input"
                  required
                  data-testid="admin-username-input"
                />
              </div>
            </div>

            <div>
              <Label style={{ color: '#2d1810' }}>Passord</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: '#8b4513' }} />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 pr-10 indian-input"
                  required
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: '#8b4513' }}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full font-bold py-6 btn-maroon transition-transform hover:scale-105"
              data-testid="admin-login-button"
            >
              {loading ? "Logger inn..." : "Logg Inn"}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t" style={{ borderColor: '#d4af37' }}></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-3" style={{ color: '#8b4513' }}>eller</span>
            </div>
          </div>

          <Button
            type="button"
            onClick={() => {
              const currentUrl = window.location.origin + '/panel050623/login';
              window.location.href = `https://demobackend.emergentagent.com/auth/v1/env/oauth/google?redirect_url=${encodeURIComponent(currentUrl)}`;
            }}
            className="w-full rounded-full font-bold py-6 transition-transform hover:scale-105 flex items-center justify-center gap-3"
            style={{ background: '#fff', color: '#2d1810', border: '2px solid #e5e7eb' }}
            data-testid="google-login-button"
          >
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Logg inn med Google
          </Button>

          <div 
            className="mt-6 p-4 rounded-lg text-center text-sm"
            style={{ background: '#f4e7d8', color: '#4a3728' }}
          >
            <p>Kun for autorisert personale</p>
            <p className="mt-2 text-xs" style={{ color: '#721111' }}>
              Kontakt administrator hvis du har glemt passordet
            </p>
          </div>
        </div>

        {/* Back to site link */}
        <div className="text-center mt-6">
          <a 
            href="/"
            className="text-sm hover:underline"
            style={{ color: '#d4af37' }}
          >
            ← Tilbake til nettsiden
          </a>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLoginPage;
